import React, { useState } from 'react';
import { useSocial } from '../SocialContext';
import { postService } from '../../../core/services/firebase/postService';
import { MediaUpload } from '../../../components/ui/MediaUpload';
import { Send, Image, X, Loader2, MapPin } from 'lucide-react';
import { BusinessSearchBar } from '../../../components/BusinessSearchBar';

interface PostComposerProps {
  onPostCreated?: () => void;
}

export const PostComposer: React.FC<PostComposerProps> = ({ onPostCreated }) => {
  const { profile } = useSocial();
  const [content, setContent] = useState('');
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [location, setLocation] = useState('');
  const [checkIn, setCheckIn] = useState<{ tenantId: string; businessName: string } | null>(null);
  const [category, setCategory] = useState<'catch' | 'technique' | 'location' | 'equipment' | 'story'>('story');
  const [tagsInput, setTagsInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMediaUpload, setShowMediaUpload] = useState(false);

  const handleAddMedia = (url: string) => {
    setMediaUrls(prev => [...prev, url]);
    setShowMediaUpload(false);
  };

  const handleRemoveMedia = (index: number) => {
    setMediaUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || (!content.trim() && mediaUrls.length === 0)) return;

    setIsSubmitting(true);
    try {
      const tags = tagsInput
        .split(/[ ,]+/)
        .map(t => t.trim().toLowerCase())
        .filter(t => t.length > 0)
        .map(t => t.startsWith('#') ? t.substring(1) : t);

      await postService.createPost({
        authorId: profile.userId || profile.handle,
        authorHandle: profile.handle,
        authorName: profile.displayName,
        authorAvatar: profile.avatarUrl,
        authorType: profile.type,
        content: content.trim() || undefined,
        mediaUrls,
        location: location.trim() || undefined,
        checkIn: checkIn || undefined,
        category,
        tags
      });

      // Reset form
      setContent('');
      setMediaUrls([]);
      setLocation('');
      setCheckIn(null);
      setTagsInput('');
      setCategory('story');
      
      if (onPostCreated) onPostCreated();
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Erro ao criar post. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!profile) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Category Selector */}
        <div className="flex flex-wrap gap-2 pb-2">
          {(['story', 'catch', 'technique', 'location', 'equipment'] as const).map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={`px-3 py-1 rounded-full text-xs font-bold capitalize transition-all ${
                category === cat 
                  ? 'bg-blue-600 text-white shadow-md scale-105' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat === 'story' ? '📖 Relato' : 
               cat === 'catch' ? '🎣 Captura' : 
               cat === 'technique' ? '💡 Técnica' : 
               cat === 'location' ? '📍 Local' : '🎒 Equipamento'}
            </button>
          ))}
        </div>

        {/* Avatar + Textarea */}
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 overflow-hidden border border-gray-100">
            {profile.avatarUrl ? (
              <img src={profile.avatarUrl} alt={profile.displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm font-bold">
                {profile.displayName[0]}
              </div>
            )}
          </div>

          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="No que você está pensando agora?"
            aria-label="Conteúdo da postagem"
            className="flex-1 resize-none border-0 focus:ring-0 text-gray-900 placeholder-gray-400 min-h-[100px] text-lg"
            disabled={isSubmitting}
          />
        </div>

        {/* Inputs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <BusinessSearchBar 
              onSelect={(b) => setCheckIn({ tenantId: b.id, businessName: b.name })}
              onClear={() => setCheckIn(null)}
            />
            {checkIn && (
              <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-lg text-blue-700 text-xs font-bold animate-in zoom-in-95 duration-200">
                <MapPin size={12} fill="currentColor" />
                <span>Check-in em: {checkIn.businessName}</span>
                <button 
                  type="button" 
                  onClick={() => setCheckIn(null)}
                  className="ml-auto hover:text-red-500"
                >
                  <X size={12} />
                </button>
              </div>
            )}
          </div>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="📍 Rio / Ponto de Pesca (Texto Livre)"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50/50 outline-none"
            disabled={isSubmitting}
          />
        </div>

        <div className="col-span-full">
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="#tags (separadas por espaço ou vírgula)"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50/50 outline-none"
            disabled={isSubmitting}
          />
        </div>

        {/* Media Preview Grid */}
        {mediaUrls.length > 0 && (
          <div className="grid grid-cols-3 gap-2 py-2">
            {mediaUrls.map((url, index) => (
              <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group shadow-sm">
                <img src={url} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => handleRemoveMedia(index)}
                  aria-label="Remover mídia"
                  className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Media Upload Area (Conditional) */}
        {showMediaUpload && (
          <div className="border-t border-gray-100 pt-4 bg-gray-50 rounded-lg p-3">
            <MediaUpload
              type="image"
              path={`posts/${profile.handle}`}
              onUploadComplete={handleAddMedia}
              className="w-full"
            />
            <button
              type="button"
              onClick={() => setShowMediaUpload(false)}
              className="mt-2 text-sm text-blue-600 font-medium hover:underline"
            >
              Concluído / Cancelar
            </button>
          </div>
        )}

        {/* Actions Row */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => setShowMediaUpload(!showMediaUpload)}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
          >
            <Image size={20} className="text-blue-500" />
            Fotos de Pesca
          </button>

          <button
            type="submit"
            disabled={isSubmitting || (!content.trim() && mediaUrls.length === 0)}
            className="flex items-center gap-2 px-8 py-2.5 bg-blue-600 text-white font-black rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-blue-200"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Publicando...
              </>
            ) : (
              <>
                <Send size={18} />
                Publicar Relato
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
